import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Subject } from 'rxjs';
import { catchError, finalize, takeUntil, timeout } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiUrl } from '../../../shared/models/apiurl.models';
import { CreditCardComponent } from '../../create-adv/select-route/credit-card/credit-card.component';
import { BusinessService, BusinessStatus } from '../../../services/business.service';

// ── Backend model shape ───────────────────────────────────────────────────────
export interface BackendPlan {
  id:             number;
  name:           string;
  description:    string;
  price:          string;    // DecimalField comes back as a string
  billing_period: 'MONTHLY' | 'YEARLY';
  duration_days:  number;
  is_active:      boolean;
  sort_order:     number;
}

// ── Grouped tier (monthly + yearly variant in one card) ───────────────────────
export interface TierGroup {
  sortOrder:  number;
  tierLabel:  string;    // Go / Plus / Pro
  tierClass:  string;    // go / plus / pro
  popular:    boolean;
  monthly:    BackendPlan | null;
  yearly:     BackendPlan | null;
}

export type BillingPeriod = 'MONTHLY' | 'YEARLY';

interface UserInfo {
  first_name: string;
  last_name:  string;
  mainPhone:  string;
  email:      string;
  is_vip:     boolean;
}

// SCRUM-120 — GET client-api/subscriptions/quota/, trimmed to the fields
// this page needs to know which tier/billing-period card is "current".
interface QuotaStatus {
  is_vip:           boolean;
  plan_id:          number | null;
  plan_name:        string | null;
  billing_period:   BillingPeriod | null;
  current_end_date: string | null;
  // SCRUM-289 — renewal window + what each plan card may offer (server-decided,
  // subscription/renewal.py; initiate/ and confirm/ enforce the same answer).
  renewal_window_days?: number;
  renewal_open?:        boolean;
  renewal_opens_at?:    string | null;
  renewal_starts_at?:   string | null;
  plan_options?:        PlanOption[];
}

/** SCRUM-289 — server verdict for one plan row. */
export interface PlanOption {
  plan_id:   number;
  action:    'subscribe' | 'upgrade' | 'renew' | 'renew_not_open' | 'downgrade';
  allowed:   boolean;
  code:      string | null;
  message:   string | null;
  starts_at: string | null;
}

/** What a tier's card should offer, relative to the caller's current plan. */
export type TierAction = 'subscribe' | 'current' | 'switch-billing' | 'upgrade' | 'downgrade'
  | 'renew'          // SCRUM-289: same tier, last days of the subscription
  | 'renew-later';   // same tier, other billing period, window not open yet

const TIER_META: { label: string; cls: string; popular: boolean }[] = [
  { label: 'Go',   cls: 'go',   popular: false },
  { label: 'Plus', cls: 'plus', popular: true  },
  { label: 'Pro',  cls: 'pro',  popular: false },
];

@Component({
    selector: 'app-vip-subscription',
    templateUrl: './vip-subscription.component.html',
    styleUrls: ['./vip-subscription.component.scss'],
    standalone: false
})
export class VipSubscriptionComponent implements OnInit, OnDestroy {

  tiers:         TierGroup[]  = [];
  billingPeriod: BillingPeriod = 'MONTHLY';

  userInfo: UserInfo = { first_name: '', last_name: '', mainPhone: '', email: '', is_vip: false };

  /** SCRUM-120 — the caller's current plan, or null when not VIP / unresolved yet. */
  currentPlanId:          number | null = null;
  currentBillingPeriod:   BillingPeriod | null = null;
  currentTierSortOrder:   number | null = null;
  currentEndDate:         string | null = null;

  /** SCRUM-289 — renewal window and per-plan verdicts from the quota call. */
  renewalOpen:       boolean = false;
  renewalOpensAt:    string | null = null;
  renewalStartsAt:   string | null = null;
  renewalWindowDays: number = 7;
  private planOptions = new Map<number, PlanOption>();

  /** Server refusal of a purchase (RENEWAL_NOT_OPEN, PLAN_DOWNGRADE_NOT_ALLOWED...). */
  actionError = '';

  /** Plan handed over by the Adaline bot (?plan_id=) — its card is highlighted. */
  highlightedTierSortOrder: number | null = null;
  private requestedPlanId: number | null = null;

  loading:    boolean = true;   // initial data load
  initiating: number | null = null;  // plan id currently being initiated

  private destroy$ = new Subject<void>();

  private get httpOptions() {
    return {
      headers: new HttpHeaders({
        'Content-Type':  'application/json',
        'Authorization': this.apiKey.getJWTToken()
      })
    };
  }

  constructor(
    private http:   HttpClient,
    private apiKey: ApiUrl,
    private dialog: MatDialog,
    private router: Router,
    private business: BusinessService,
    private route: ActivatedRoute,
  ) {}

  /** SCRUM-265 — resolved state; `userInfo.is_vip` stays true after a lapse. */
  bizStatus: BusinessStatus | null = null;

  ngOnInit(): void {
    const raw = Number(this.route.snapshot.queryParamMap.get('plan_id'));
    this.requestedPlanId = Number.isInteger(raw) && raw > 0 ? raw : null;
    this.loadAll();
    this.business.status().subscribe(s => (this.bizStatus = s));
  }

  /** Live VIP (not the raw flag). */
  get isVipNow(): boolean {
    return this.bizStatus ? this.bizStatus.is_vip : !!this.userInfo?.is_vip;
  }

  /**
   * SCRUM-260 — right after a successful purchase, offer the (optional)
   * business profile. Only when vip_until actually moved, so closing the
   * payment dialog without paying never redirects.
   */
  private afterCheckout(vipUntilBefore: string | null): void {
    this.reloadQuota();
    this.business.status().subscribe(s => {
      if (!s) return;
      const purchased = s.is_vip && s.vip_until !== vipUntilBefore;
      this.bizStatus = s;
      if (purchased && s.should_prompt_profile) {
        this.router.navigate(['/personal/store'], { queryParams: { onboarding: 1 } });
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Data loading ──────────────────────────────────────────────────────────

  private loadAll(): void {
    this.loading = true;
    let done = 0;
    const total = 3;
    const check = () => { if (++done === total) this.resolveCurrentTier(); };

    // Load user info
    this.http.get<UserInfo>(this.apiKey.getUserInfo(), this.httpOptions)
      .pipe(timeout(10000), takeUntil(this.destroy$), catchError(e => throwError(e.error || e)), finalize(check))
      .subscribe({ next: res => { this.userInfo = res; }, error: () => {} });

    // Load subscription plans
    this.http.get<BackendPlan[]>(this.apiKey.getSubscriptionPlans(), this.httpOptions)
      .pipe(timeout(10000), takeUntil(this.destroy$), catchError(e => throwError(e.error || e)), finalize(check))
      .subscribe({ next: res => { this.buildTiers(res || []); }, error: () => {} });

    // SCRUM-120 — which plan (if any) is currently active, so the matching
    // card can be marked instead of every tier looking identical.
    this.http.get<QuotaStatus>(this.apiKey.getQuotaUrl(), this.httpOptions)
      .pipe(timeout(10000), takeUntil(this.destroy$), catchError(e => throwError(e.error || e)), finalize(check))
      .subscribe({
        next: res => {
          this.currentPlanId        = res?.is_vip ? res.plan_id : null;
          this.currentBillingPeriod = res?.is_vip ? res.billing_period : null;
          this.currentEndDate       = res?.is_vip ? res.current_end_date : null;
          this.applyRenewal(res);
        },
        error: () => {},
      });
  }

  private applyRenewal(res: QuotaStatus | null): void {
    this.renewalOpen       = !!res?.is_vip && !!res?.renewal_open;
    this.renewalOpensAt    = res?.is_vip ? (res.renewal_opens_at ?? null) : null;
    this.renewalStartsAt   = res?.is_vip ? (res.renewal_starts_at ?? null) : null;
    this.renewalWindowDays = res?.renewal_window_days ?? 7;
    this.planOptions = new Map((res?.plan_options || []).map(o => [o.plan_id, o]));
  }

  /** End of the paid period (vip_until), falling back to the plan row's own end. */
  get paidUntil(): string | null {
    return this.renewalStartsAt || this.currentEndDate;
  }

  /** Re-read plan / renewal state (after a purchase the window closes). */
  private reloadQuota(): void {
    this.http.get<QuotaStatus>(this.apiKey.getQuotaUrl(), this.httpOptions)
      .pipe(timeout(10000), takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.currentPlanId        = res?.is_vip ? res.plan_id : null;
          this.currentBillingPeriod = res?.is_vip ? res.billing_period : null;
          this.currentEndDate       = res?.is_vip ? res.current_end_date : null;
          this.applyRenewal(res);
          this.resolveCurrentTier();
        },
        error: () => {},
      });
  }

  /** Once tiers + current plan are both loaded, resolve which tier that plan belongs to. */
  private resolveCurrentTier(): void {
    this.loading = false;
    this.applyRequestedPlan();
    if (this.currentPlanId == null) { this.currentTierSortOrder = null; return; }
    const owning = this.tiers.find(t => t.monthly?.id === this.currentPlanId || t.yearly?.id === this.currentPlanId);
    this.currentTierSortOrder = owning ? owning.sortOrder : null;
  }

  private buildTiers(plans: BackendPlan[]): void {
    // Group plans by sort_order → each sort_order = one tier card
    const map = new Map<number, { monthly: BackendPlan | null; yearly: BackendPlan | null }>();

    for (const p of plans) {
      if (!map.has(p.sort_order)) map.set(p.sort_order, { monthly: null, yearly: null });
      const entry = map.get(p.sort_order)!;
      if (p.billing_period === 'MONTHLY') entry.monthly = p;
      else                                 entry.yearly  = p;
    }

    // Sort by sort_order and assign tier meta
    const sorted = [...map.entries()].sort((a, b) => a[0] - b[0]);
    this.tiers = sorted.map(([sortOrder, { monthly, yearly }], i) => ({
      sortOrder,
      tierLabel: TIER_META[i]?.label ?? `Tier ${i + 1}`,
      tierClass: TIER_META[i]?.cls   ?? 'go',
      popular:   TIER_META[i]?.popular ?? false,
      monthly,
      yearly,
    }));
  }

  /**
   * Adaline "כמה עולה פרסום?" -> plan card -> /personal/vip-subscription?plan_id=<id>.
   * The id is the row of the billing period the user had selected in the bot,
   * so switch this page to that period and highlight that tier's card. Does
   * NOT start checkout by itself — the user still confirms with the card's button.
   */
  private applyRequestedPlan(): void {
    if (this.requestedPlanId == null) return;
    const id = this.requestedPlanId;
    this.requestedPlanId = null;
    const tier = this.tiers.find(t => t.monthly?.id === id || t.yearly?.id === id);
    if (!tier) return;
    this.billingPeriod = tier.yearly?.id === id ? 'YEARLY' : 'MONTHLY';
    this.highlightedTierSortOrder = tier.sortOrder;
    setTimeout(() => document.querySelector('.vs-card--requested')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  activePlan(tier: TierGroup): BackendPlan | null {
    return this.billingPeriod === 'MONTHLY' ? tier.monthly : tier.yearly;
  }

  /** Per-month display price.
   *  Monthly plan → price as-is.
   *  Yearly plan  → total price ÷ 12 (backend stores the full annual charge). */
  displayPrice(tier: TierGroup): string {
    const p = this.activePlan(tier);
    if (!p) return '—';
    const val = this.billingPeriod === 'YEARLY'
      ? parseFloat(p.price) / 12
      : parseFloat(p.price);
    return Math.round(val).toString();
  }

  /** Original monthly price (for the strikethrough line in yearly mode). */
  monthlyPrice(tier: TierGroup): string {
    return tier.monthly ? Math.round(parseFloat(tier.monthly.price)).toString() : '—';
  }

  /** Total billed once per year (= yearly plan price as stored in DB). */
  yearlyTotal(tier: TierGroup): string {
    return tier.yearly ? Math.round(parseFloat(tier.yearly.price)).toString() : '—';
  }

  /** % saved vs 12 × monthly price. */
  savingPct(tier: TierGroup): number {
    if (!tier.monthly || !tier.yearly) return 0;
    const full    = parseFloat(tier.monthly.price) * 12;
    const reduced = parseFloat(tier.yearly.price);
    return Math.round(((full - reduced) / full) * 100);
  }

  features(tier: TierGroup): string[] {
    const p = this.activePlan(tier) ?? tier.monthly ?? tier.yearly;
    if (!p?.description) return [];
    return p.description.split(/\r\n|\r|\n/).map(l => l.trim()).filter(Boolean);
  }

  toggleBilling(p: BillingPeriod): void { this.billingPeriod = p; }

  // ── Current plan / upgrade state (SCRUM-120) ────────────────────────────

  /**
   * What this tier's card should offer, relative to the caller's own plan.
   * Deliberately compares `sortOrder` (Go < Plus < Pro), not price, since
   * that is the same ranking the plans list itself is built and displayed
   * in — no separate "which plan is better" definition to keep in sync.
   */
  tierAction(tier: TierGroup): TierAction {
    // SCRUM-289: the server's verdict for this exact plan row wins — it is
    // what initiate/ and confirm/ will enforce. The local comparison below is
    // only the fallback for an older backend without plan_options.
    const plan = this.activePlan(tier);
    const opt = plan ? this.planOptions.get(plan.id) : undefined;
    if (opt && this.isVipNow) {
      switch (opt.action) {
        case 'renew':          return 'renew';
        case 'renew_not_open': return plan!.id === this.currentPlanId ? 'current' : 'renew-later';
        case 'upgrade':        return 'upgrade';
        case 'downgrade':      return 'downgrade';
        default:               return 'subscribe';
      }
    }
    if (!this.isVipNow || this.currentTierSortOrder == null) return 'subscribe';
    if (tier.sortOrder === this.currentTierSortOrder) {
      const plan = this.activePlan(tier);
      return plan && plan.id === this.currentPlanId ? 'current' : 'switch-billing';
    }
    return tier.sortOrder > this.currentTierSortOrder ? 'upgrade' : 'downgrade';
  }

  ctaLabel(tier: TierGroup): string {
    switch (this.tierAction(tier)) {
      case 'current':        return 'התוכנית הנוכחית שלך';
      case 'renew':          return this.billingPeriod === 'YEARLY' ? 'חדש לשנה' : 'חדש לחודש';
      case 'renew-later':    return this.renewalOpensAt
                               ? `החידוש ייפתח ב-${this.heDate(this.renewalOpensAt)}`
                               : 'החידוש ייפתח לקראת סיום המנוי';
      case 'switch-billing':  return this.billingPeriod === 'YEARLY' ? 'עבור למסלול שנתי' : 'עבור למסלול חודשי';
      case 'upgrade':         return 'שדרג לתוכנית זו';
      case 'downgrade':       return 'כבר יש לך תוכנית טובה יותר';
      default:                return this.billingPeriod === 'YEARLY' ? 'התחל שנה VIP' : 'התחל חודש VIP';
    }
  }

  ctaDisabled(tier: TierGroup): boolean {
    const action = this.tierAction(tier);
    return action === 'current' || action === 'downgrade' || action === 'renew-later'
      || this.initiating !== null;
  }

  /** Same tier as the caller's plan (any billing period) — gets the "your plan" badge. */
  isOwnTier(tier: TierGroup): boolean {
    const a = this.tierAction(tier);
    return a === 'current' || a === 'switch-billing' || a === 'renew' || a === 'renew-later';
  }

  heDate(iso: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  subscribe(tier: TierGroup): void {
    const plan = this.activePlan(tier);
    if (!plan || this.initiating) return;
    // Defensive: the button is already disabled for these, but a stale
    // render (e.g. a click queued right as data loaded) should not be able
    // to slip a request through.
    const action = this.tierAction(tier);
    if (action === 'current' || action === 'downgrade' || action === 'renew-later') return;

    this.initiating = plan.id;
    this.actionError = '';

    this.http.post<{ session_id: string; session_url: string; plan: BackendPlan }>(
      this.apiKey.initiateSubscription(),
      { plan_id: plan.id },
      this.httpOptions
    )
    .pipe(
      timeout(15000),
      takeUntil(this.destroy$),
      catchError(e => throwError(e.error || e)),
      finalize(() => { this.initiating = null; })
    )
    .subscribe({
      next: res => {
        // Build an adsRoute-shaped object so CreditCardPaymentComponent can display it
        const adsRouteProxy = {
          id:          plan.id,
          title:       `${plan.name} — ${tier.tierLabel}`,
          description: plan.description,
          price:       parseFloat(plan.price),
          lifeSpan:    plan.duration_days,
          image:       '',
          level:       null,
        };

        const vipUntilBefore = this.bizStatus?.vip_until ?? null;
        this.dialog.open(CreditCardComponent, {
          data: {
            adsRoute:         adsRouteProxy,
            adsId:            null,
            isSubscription:   true,
            planId:           plan.id,
            sessionId:        res.session_id,
            billingPeriod:    plan.billing_period,
            durationDays:     plan.duration_days,
          },
          width:      '760px',
          direction:  'rtl',
          panelClass: 'vip-payment-dialog',
        }).afterClosed().subscribe(() => this.afterCheckout(vipUntilBefore));
      },
      // SCRUM-289: the server re-checks the window / downgrade rule; show why.
      error: (e: any) => {
        this.actionError = e?.message || 'לא הצלחנו לפתוח את התשלום, נסו שוב.';
        this.reloadQuota();   // the cards were stale — redraw from the server's answer
      }
    });
  }

  goToDashboard(): void {
    this.router.navigate(['/personal/vip-dashboard']);
  }
}
